import { Home, FileText, FormInput, Database, Workflow, Star, Users, Users2, Settings, X } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from 'react-query'
import { api } from '../lib/api'

interface SidebarProps {
  isMobile?: boolean
  isOpen?: boolean
  onClose?: () => void
}

export default function Sidebar({ isMobile = false, isOpen = true, onClose }: SidebarProps) {
  const location = useLocation()
  
  // Get current URL search parameters to preserve them in navigation
  const searchParams = new URLSearchParams(location.search)
  searchParams.delete('workflowRecord')
  searchParams.delete('mode')
  const queryString = searchParams.toString()
  const queryPrefix = queryString ? `?${queryString}` : ''

  // Fetch current user to check nexus organization membership
  const { data: currentUser } = useQuery(
    ['current-user'], // Use array format for consistency
    () => api.get('/users/me').then(res => res.data.data), // Already correct format
    { 
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      retry: 1 
    }
  )

  // Check if user is in nexus organization
  const isNexusAdmin = currentUser?.data?.profile?.organizationId === 'nexus' || 
                       currentUser?.profile?.organizationId === 'nexus' ||
                       currentUser?.organizationId === 'nexus'
  
  // Check if user is in nexus organization (multiple possible data structures)
  const debugOrgId = currentUser?.data?.profile?.organizationId || 
                     currentUser?.profile?.organizationId || 
                     currentUser?.organizationId

  const navigation = [
    { 
      name: 'My Home', 
      href: `/${queryPrefix}`, // Always go to root, let App.tsx handle the starting page logic
      icon: Home,
      disabled: false
    },
    { name: 'Pages', href: `/pages${queryPrefix}`, icon: FileText, disabled: false },
    { name: 'Forms', href: `/forms${queryPrefix}`, icon: FormInput, disabled: false },
    { name: 'Groups', href: `/groups${queryPrefix}`, icon: Users2, disabled: false },
    { name: 'Registers', href: `/registers${queryPrefix}`, icon: Database, disabled: true },
    { name: 'Workflows', href: `/workflows${queryPrefix}`, icon: Workflow, disabled: false },
    { name: 'Favourites', href: `/favourites${queryPrefix}`, icon: Star, disabled: true },
    { name: 'Organisation', href: `/organisation${queryPrefix}`, icon: Users, disabled: false },
    ...(isNexusAdmin ? [{ name: 'Nexus Admin', href: `/admin${queryPrefix}`, icon: Settings, disabled: false }] : []),
  ]

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}
      
      <div className={`${isMobile ? 'fixed inset-y-0 left-0 z-50 w-64' : 'w-64'} border-r bg-background`}>
      {/* Mobile close button */}
      {isMobile && onClose && (
        <div className="flex justify-end p-4 border-b">
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md"
            title="Close sidebar"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      )}
      
      <div className="p-4">
        <div className="text-center">
          <p className="text-sm text-gray-500 mb-2">Create pages by adding</p>
          <p className="text-sm text-gray-500">nested page tiles</p>
        </div>
      </div>
      
      <nav className="px-4">
        <ul className="space-y-1">
          {navigation.map((item) => {
            // Special handling for home page active state
            const itemPath = item.href.split('?')[0]
            const isActive = item.name === 'My Home' 
              ? (location.pathname === '/')
              : location.pathname === itemPath
            
            const isDisabled = item.disabled
            const isNexusAdminLink = item.name === 'Nexus Admin'
            
            return (
              <li key={item.name}>
                {isDisabled ? (
                  <div
                    className="flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium cursor-not-allowed opacity-50 text-gray-400"
                    title="Coming soon - under development"
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.name}</span>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : isNexusAdminLink
                        ? 'text-red-600 hover:text-red-700 hover:bg-red-50'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <item.icon className={`h-4 w-4 ${isNexusAdminLink ? 'text-red-600' : ''}`} />
                    <span>{item.name}</span>
                  </Link>
                )}
              </li>
            )
          })}
        </ul>
      </nav>
      </div>
    </>
  )
}
